/**
 * useLooseRelationshipPeek — live counts + 5-row sample for the loose-FK editor.
 *
 * - Source count fires only when sourceFilter has at least one condition.
 * - Target count + sample always fire (filter is optional; empty filter = total rows).
 * - Both queries are debounced 400 ms after the last filter edit via a local
 *   useDebouncedValue helper that delays the values used in the queryKey.
 *
 * Note: limit: 1 is used for the source count query (not 0) to ensure the data
 * query returns a valid result set. The COUNT query runs in parallel and always
 * returns totalRowCount regardless of limit.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createExecutionId } from '../../../../types/queryExecution';
import { fetchTableExplorerRows } from '../../table-explorer/services/tableExplorerApi';
import type { FilterGroup } from '../../core/types/workspaceTypes';

export type LooseRelationshipPeekParams = {
  connectionId: string;
  database: string;
  source: { schema: string; table: string; filter?: FilterGroup[] };
  target: {
    schema: string;
    table: string;
    keyColumn: string;
    displayColumn?: string;
    filter?: FilterGroup[];
  };
  enabled: boolean;
};

const DEBOUNCE_MS = 400;
const SAMPLE_LIMIT = 5;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return v;
}

export function useLooseRelationshipPeek(params: LooseRelationshipPeekParams) {
  const { connectionId, database, source, target, enabled } = params;

  const debouncedSourceFilter = useDebouncedValue(source.filter, DEBOUNCE_MS);
  const debouncedTargetFilter = useDebouncedValue(target.filter, DEBOUNCE_MS);

  const sourceHasConditions = !!source.filter && source.filter.length > 0;

  const sourceQuery = useQuery({
    queryKey: [
      'loose-fk-peek-source',
      connectionId,
      database,
      source.schema,
      source.table,
      debouncedSourceFilter,
    ],
    queryFn: async () => {
      const result = await fetchTableExplorerRows({
        connectionId,
        executionId: createExecutionId(`peek-src-${source.table}`),
        table: { database, schema: source.schema, tableName: source.table },
        columns: '*',
        // Use limit: 1 not 0 — the engine may generate TOP 0 / LIMIT 0 SQL which
        // some engines optimise away (returning no result set). The COUNT sub-query
        // always runs in parallel and is what we care about; one data row is harmless.
        limit: 1,
        filters: debouncedSourceFilter ?? [],
        sorting: [],
      });
      if (!result.success) throw new Error(result.error || 'source count failed');
      return result.totalRowCount ?? 0;
    },
    enabled: enabled && sourceHasConditions,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const targetColumns = useMemo(
    () => [target.keyColumn, ...(target.displayColumn ? [target.displayColumn] : [])],
    [target.keyColumn, target.displayColumn],
  );

  const targetQuery = useQuery({
    queryKey: [
      'loose-fk-peek-target',
      connectionId,
      database,
      target.schema,
      target.table,
      targetColumns,
      debouncedTargetFilter,
    ],
    queryFn: async () => {
      const result = await fetchTableExplorerRows({
        connectionId,
        executionId: createExecutionId(`peek-tgt-${target.table}`),
        table: { database, schema: target.schema, tableName: target.table },
        columns: targetColumns,
        limit: SAMPLE_LIMIT,
        filters: debouncedTargetFilter ?? [],
        sorting: [],
      });
      if (!result.success) throw new Error(result.error || 'target peek failed');
      return {
        count: result.totalRowCount ?? 0,
        rows: result.rows ?? [],
        columns: (result.columns ?? []).map((c) => ({ name: c.name, dataType: c.dataType ?? '' })),
      };
    },
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  return {
    source: {
      count: sourceQuery.data,
      isLoading: sourceQuery.isFetching,
      error: sourceQuery.error,
      enabled: sourceHasConditions,
    },
    target: {
      count: targetQuery.data?.count ?? 0,
      rows: targetQuery.data?.rows ?? [],
      columns: targetQuery.data?.columns ?? [],
      isLoading: targetQuery.isFetching,
      error: targetQuery.error,
    },
    refetch: () => {
      void sourceQuery.refetch();
      void targetQuery.refetch();
    },
  };
}
